'use client';

import { useEffect, useState } from 'react';
import { LegacyDialog, Button, Text } from '@terra/ui-design-system';
import { VStack, Box } from '@terra/ui-styled-system';
import { Alert01Icon, Clock01Icon } from '@terra/ui-icons';
import { useRouter } from 'next/navigation';
import { clearCurrentTenantId } from '../../store/tenantStorage';
import { clearTorchNotSupportedToast } from '../../hooks/torch-not-supported-toast';

/**
 * Inactivity Warning Dialog
 * Shows a warning when user has been inactive and will be logged out soon
 */
export function InactivityWarningDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const router = useRouter();
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI;

  useEffect(() => {
    if (!isElectron || !window.electronAPI?.session) {
      return;
    }

    // Listen for inactivity warning from Electron (optional for old Electron)
    const cleanupWarning = window.electronAPI.session?.onInactivityWarning?.(
      (data) => {
        setRemainingTime(data.remainingTime);
        setIsOpen(true);
      },
    );
    // Listen for automatic logout (optional for old Electron)
    const cleanupLogout = window.electronAPI.session?.onLoggedOut?.((data) => {
      clearCurrentTenantId();
      setIsOpen(false);
      router.push(`/sign-in?reason=${encodeURIComponent(data.reason)}`);
    });
    // Close dialog if inactivity monitoring is turned off (new API, optional for old Electron)
    const cleanupMonitorChanged =
      window.electronAPI.session?.onInactivityMonitorChanged?.((data) => {
        if (!data.enabled) {
          setIsOpen(false);
        }
      });

    return () => {
      cleanupWarning?.();
      cleanupLogout?.();
      cleanupMonitorChanged?.();
    };
  }, [isElectron, router]);
  // Update remaining time countdown
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    let intervalId: ReturnType<typeof setInterval> | undefined;

    intervalId = setInterval(() => {
      setRemainingTime((prev) => {
        const next = Math.max(0, prev - 1000);

        if (next === 0 && intervalId !== undefined) {
          clearInterval(intervalId);
          intervalId = undefined;
        }

        return next;
      });
    }, 1000);

    return () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
    };
  }, [isOpen]);
  const handleExtendSession = async () => {
    if (window.electronAPI?.session?.extend) {
      await window.electronAPI.session.extend();
      setIsOpen(false);
    }
  };

  const handleLogout = async () => {
    clearTorchNotSupportedToast();
    clearCurrentTenantId();
    if (window.electronAPI?.auth?.logout) {
      await window.electronAPI.auth.logout();
      setIsOpen(false);
    }
  };

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Only render in Electron
  if (!isElectron) {
    return null;
  }

  return (
    <LegacyDialog.Root open={isOpen} onOpenChange={() => setIsOpen(false)}>
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="500px">
          <VStack gap={6} p={6} alignItems="stretch">
            {/* Header */}
            <VStack gap={3} alignItems="center">
              <Box color="fg.warning">
                <Alert01Icon size={48} />
              </Box>
              <LegacyDialog.Title textAlign="center">
                Session Expiring Soon
              </LegacyDialog.Title>
            </VStack>

            {/* Body */}
            <VStack gap={4} alignItems="center">
              <Text color="fg.muted" textAlign="center">
                You've been inactive for a while. For your security, you'll be
                automatically logged out in:
              </Text>

              <Box
                p={4}
                bg="bg.subtle"
                borderRadius="md"
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Clock01Icon size={24} />
                <Text fontSize="2xl" fontWeight="bold">
                  {formatTime(remainingTime)}
                </Text>
              </Box>

              <Text fontSize="sm" color="fg.muted" textAlign="center">
                Click "Stay Logged In" to continue your session, or "Logout" to
                sign out now.
              </Text>
            </VStack>

            {/* Footer */}
            <Box display="flex" gap={3} justifyContent="flex-end">
              <Button variant="outline" onClick={handleLogout}>
                Logout
              </Button>
              <Button onClick={handleExtendSession}>Stay Logged In</Button>
            </Box>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
